import { Inspection, InspectionHistory, Checklist } from "../services/inspection.service";

export interface User {
    id: number;
    willowId: string;
    name: string;
    email: string;
    role: string;
    companyName: string;
    companyAddress: string;
    phoneNumber: string;
    yardName: string;
    yardLocation: string;
    dot: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface InspectionState {
    currentInspection: Inspection | null;
    completedInspection: Inspection | null;
    currentChecklists: Checklist[];
    inspectionHistory: InspectionHistory[];
    hasCriticalEvent: boolean;
    isPolling: boolean;
    pollingInterval: number | null;
    previousInspection: Inspection | null;
    showCriticalModal: boolean;
    hasModelOpen: boolean;
    hasNewActivity: boolean;
    lastActivityCheck: string | null;
}

export interface RootState {
    token: string;
    user: User | null;
    inspection: InspectionState;
}

const initialState: RootState = {
    token: '',
    user: null,
    inspection: {
        currentInspection: null,
        completedInspection: null,
        currentChecklists: [],
        inspectionHistory: [],
        hasCriticalEvent: false,
        isPolling: false,
        pollingInterval: null,
        previousInspection: null,
        showCriticalModal: false,
        hasModelOpen: false,
        hasNewActivity: false,
        lastActivityCheck: null
    }
};

function rootReducer(state: RootState = initialState, action: any): RootState {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                token: action.payload.token,
                user: action.payload.user
            };
        case 'LOGOUT':
            return {
                ...state,
                token: '',
                user: null
            };

        // Inspection cases
        case 'SET_CURRENT_INSPECTION':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    currentInspection: action.payload
                }
            };

        case 'SET_COMPLETED_INSPECTION':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    completedInspection: action.payload
                }
            };

        case 'SET_CURRENT_CHECKLISTS':
            const hasFailedItems = action.payload.some((checklist: Checklist) => checklist.status === 'failed');
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    currentChecklists: action.payload,
                    hasCriticalEvent: hasFailedItems
                }
            };

        case 'SET_INSPECTION_HISTORY':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    inspectionHistory: action.payload
                }
            };

        case 'SET_HAS_CRITICAL_EVENT':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    hasCriticalEvent: action.payload
                }
            };

        case 'START_POLLING':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    isPolling: true,
                    pollingInterval: action.payload
                }
            };

        case 'STOP_POLLING':
            if (state.inspection.pollingInterval) {
                clearInterval(state.inspection.pollingInterval);
            }
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    isPolling: false,
                    pollingInterval: null
                }
            };

        case 'UPDATE_PREVIOUS_INSPECTION':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    previousInspection: action.payload
                }
            };

        case 'CLEAR_INSPECTION_DATA':
            if (state.inspection.pollingInterval) {
                clearInterval(state.inspection.pollingInterval);
            }
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    currentInspection: null,
                    completedInspection: null,
                    currentChecklists: [],
                    hasCriticalEvent: false,
                    previousInspection: null,
                    isPolling: false,
                    pollingInterval: null,
                    showCriticalModal: false
                }
            };

        case 'SET_SHOW_CRITICAL_MODAL':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    showCriticalModal: action.payload
                }
            };
        case 'MODAL_OPEN':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    hasModelOpen: action.payload
                }
            };

        case 'SET_HAS_NEW_ACTIVITY':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    hasNewActivity: action.payload
                }
            };

        case 'CLEAR_ACTIVITY_NOTIFICATION':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    hasNewActivity: false
                }
            };

        case 'SET_LAST_ACTIVITY_CHECK':
            return {
                ...state,
                inspection: {
                    ...state.inspection,
                    lastActivityCheck: action.payload
                }
            };

        default:
            return state;
    }
}

export default rootReducer;